import { useState } from "react";
import SelectBox from "../../common/components/SelectBox";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";

interface QuizSearchFiltersProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  statusFilter: string;
  onStatusChange: (value: string) => void;
  typeFilter: string;
  onTypeChange: (value: string) => void;
  difficultyFilter: string;
  onDifficultyChange: (value: string) => void;
  difficultyOptions: { label: string; value: string }[];
  generationNumber: string;
  onGenerationNumberChange: (value: string) => void;
  dateFieldFilter: string;
  onDateFieldFilterChange: (value: string) => void;
  dateRange: string;
  onDateRangeChange: (value: string) => void;
  customDateFrom: string;
  onCustomDateFromChange: (value: string) => void;
  customDateTo: string;
  onCustomDateToChange: (value: string) => void;
  onSearch: () => void;
  onCreateQuiz: () => void;
  onReset: () => void;
  totalCount?: number;
}

export default function QuizSearchFilters({
  searchQuery,
  onSearchChange,
  statusFilter,
  onStatusChange,
  typeFilter,
  onTypeChange,
  difficultyFilter,
  onDifficultyChange,
  difficultyOptions,
  generationNumber,
  onGenerationNumberChange,
  dateFieldFilter,
  onDateFieldFilterChange,
  dateRange,
  onDateRangeChange,
  customDateFrom,
  onCustomDateFromChange,
  customDateTo,
  onCustomDateToChange,
  onSearch,
  onCreateQuiz,
  onReset,
  totalCount,
}: QuizSearchFiltersProps) {
  const statusOptions = [
    { label: "전체상태", value: "" },
    { label: "활성", value: "활성" },
    { label: "비활성", value: "비활성" },
    { label: "신규", value: "신규" },
    { label: "반려", value: "반려" },
  ];

  const typeOptions = [
    { label: "전체유형", value: "" },
    { label: "객관식", value: "객관식" },
    { label: "O/X", value: "O/X" },
    { label: "단답형", value: "단답형" },
  ];

  const dateRangeOptions = [
    { label: "전체", value: "전체" },
    { label: "오늘", value: "오늘" },
    { label: "3일", value: "3일" },
    { label: "7일", value: "7일" },
    { label: "1개월", value: "1개월" },
    { label: "6개월", value: "6개월" },
    { label: "1년", value: "1년" },
    { label: "직접선택", value: "직접선택" },
  ];

  const dateFieldOptions = [
    { label: "생성일자", value: "createdAt" },
    { label: "수정일자", value: "updatedAt" },
  ];

  const [isFilterVisible, setIsFilterVisible] = useState(true);

  return (
    <div className="flex flex-col gap-4 rounded-lg bg-white p-6 shadow-md">
      {/* 첫 번째 행: 검색창, 검색버튼, 총 개수, 필터설정, + 퀴즈 생성 버튼 */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 flex-1">
          <div className="flex items-center gap-2 max-w-xl">
            <input
              type="text"
              placeholder="검색어를 입력하세요..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-[400px] h-[40px] px-4 bg-white border border-solid border-zinc-300 rounded-md focus:outline-none focus:border-black"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onSearch();
                }
              }}
            />
            <button
              onClick={onSearch}
              className="whitespace-nowrap rounded bg-black px-4 h-[40px] text-sm font-medium text-white transition-colors hover:bg-gray-800"
            >
              검색
            </button>
          </div>
          {totalCount !== undefined && totalCount > 0 && (
            <span className="text-sm text-gray-600">
              총 {totalCount.toLocaleString()}개
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={onCreateQuiz}
            className="whitespace-nowrap rounded bg-blue-500 px-4 h-[40px] text-sm font-medium text-white transition-colors hover:bg-blue-600"
          >
            + 퀴즈 생성
          </button>
          <button
            onClick={() => setIsFilterVisible(!isFilterVisible)}
            className="flex items-center gap-1 whitespace-nowrap rounded bg-black px-4 h-[40px] text-sm font-medium text-white transition-colors hover:bg-gray-800"
          >
            필터설정
            {isFilterVisible ? (
              <ChevronUpIcon className="h-4 w-4" />
            ) : (
              <ChevronDownIcon className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>

      {/* 필터 영역 (토글 가능) */}
      {isFilterVisible && (
        <>
          {/* 두 번째 행: 상태, 유형, 난이도, 생성번호 */}
          <div className="flex flex-wrap items-center gap-2">
            <SelectBox
              value={statusFilter}
              onSelect={onStatusChange}
              options={statusOptions}
              className="min-w-[120px]"
            />

            <SelectBox
              value={typeFilter}
              onSelect={onTypeChange}
              options={typeOptions}
              className="min-w-[120px]"
            />

            <SelectBox
              value={difficultyFilter}
              onSelect={onDifficultyChange}
              options={difficultyOptions}
              className="min-w-[120px]"
            />

            <input
              type="text"
              placeholder="생성번호 입력"
              value={generationNumber}
              onChange={(e) => onGenerationNumberChange(e.target.value)}
              className="min-w-[160px] h-[40px] px-3 bg-white border border-solid border-zinc-300 rounded-md focus:outline-none focus:border-black"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onSearch();
                }
              }}
            />
          </div>

          {/* 세 번째 행: 기간 기준 선택, 날짜 범위 옵션들, 초기화 버튼 */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">기간:</span>
                <SelectBox
                  value={dateFieldFilter}
                  onSelect={onDateFieldFilterChange}
                  options={dateFieldOptions}
                  className="min-w-[120px]"
                />
              </div>

              <div className="flex gap-2">
                {dateRangeOptions.map((option) => {
                  const isSelected = dateRange === option.value;
                  return (
                    <button
                      key={option.value}
                      onClick={() => onDateRangeChange(option.value)}
                      className={`rounded px-3 h-[40px] text-sm font-medium transition-colors ${
                        isSelected
                          ? "bg-blue-500 text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>

              {dateRange === "직접선택" && (
                <div className="flex items-center gap-2 rounded border border-gray-300 px-3 h-[40px]">
                  <input
                    type="date"
                    value={customDateFrom}
                    onChange={(e) => onCustomDateFromChange(e.target.value)}
                    className="border-none outline-none text-sm bg-transparent"
                  />
                  <span className="text-gray-500">~</span>
                  <input
                    type="date"
                    value={customDateTo}
                    onChange={(e) => onCustomDateToChange(e.target.value)}
                    className="border-none outline-none text-sm bg-transparent"
                  />
                </div>
              )}
            </div>

            <button
              onClick={onReset}
              className="whitespace-nowrap rounded bg-black px-4 h-[40px] text-sm font-medium text-white transition-colors hover:bg-gray-800"
            >
              초기화
            </button>
          </div>
        </>
      )}
    </div>
  );
}